import React, { useEffect, useMemo } from 'react';
import {
  ApiSubscriptionStates,
  SubscriptionType
} from '@uma/react-commons/dist/index';

import { FeatureFlagProps } from 'src/App';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { getLoginAndSecurityCard, getOtherLinksCard } from './cardDefinitions';
import FrameCard, {
  IFrameCardProps,
  IFrameCardLoadingProps
} from './FrameCardTemplate';
import { Frame } from './styles';

import { useFrameCardsLoader } from './hooks/useFrameCardsLoader';
import { getCardKey } from './utils/getCardKey';
import { handleSubscriptionCardsAnalyticsEvents } from './analytics';

export interface SubscriptionCounts {
  totalActiveInstantInkCt: number;
  totalInactiveInstantInkCt: number;
  totalActiveHpAllInCt: number;
  totalInactiveHpAllInCt: number;
  totalActiveSubs: number;
  totalInactiveSubs: number;
}

export interface Subscription {
  state: ApiSubscriptionStates;
  type: SubscriptionType;
}

export interface FrameCardsProps {
  subscriptions: Subscription[] | null | undefined;
  isSubscriptionsLoading: boolean;
  featureFlags: FeatureFlagProps;
}

const FrameCards = ({
  subscriptions,
  isSubscriptionsLoading,
  featureFlags
}: FrameCardsProps) => {
  const { translate, northboundAPIs } = useDependencyManagerContext();

  const language = northboundAPIs?.v1.localization.language || '';
  const country = northboundAPIs?.v1.localization.country || '';

  const {
    loadedCards,
    allCardsResolved,
    subscriptionCounts,
    instantInkSubDifferentThanInactiveExists,
    hpAllInSubDifferentThanInactiveExists
  } = useFrameCardsLoader({
    subscriptions,
    isSubscriptionsLoading,
    translate,
    language,
    country,
    featureFlags
  });

  const { isLoading, securityHPIDPassword: flagChangePassword } = featureFlags;

  const cards = useMemo(() => {
    const fixed: IFrameCardProps[] = [
      getLoginAndSecurityCard(translate, flagChangePassword, isLoading),
      getOtherLinksCard(translate)
    ];

    const loadingCard: IFrameCardLoadingProps = { loading: true };

    return allCardsResolved
      ? [...fixed, ...loadedCards]
      : [...fixed, ...loadedCards, loadingCard];
  }, [loadedCards, allCardsResolved, translate, flagChangePassword, isLoading]);

  useEffect(() => {
    if (allCardsResolved) {
      handleSubscriptionCardsAnalyticsEvents({
        instantInkSubDifferentThanInactiveExists,
        hpAllInSubDifferentThanInactiveExists,
        subscriptionCounts
      });
    }
  }, [
    allCardsResolved,
    instantInkSubDifferentThanInactiveExists,
    hpAllInSubDifferentThanInactiveExists,
    subscriptionCounts
  ]);

  return (
    <Frame data-testid="frame-cards">
      {cards.map((card) => (
        <FrameCard
          key={getCardKey(card)}
          card={card}
        />
      ))}
    </Frame>
  );
};

export default FrameCards;
